// Layout overrides for the generated settings pages (plan §4.7): one per legacy settings page, pinning its section
// order, headings and copy (legacy text verbatim, its typos included: "the label that want displayed"). Values,
// types, defaults and permissions all come from the registry.
import type { SettingsLayout } from './registry-form.ts';

const TIPS_TABS = [
  { label: 'Customer Tips', to: '/l/$slug/settings/order-checkout/tips/customer-tips' },
  { label: 'Staff Tips', to: '/l/$slug/settings/order-checkout/tips/staff-tips' },
] as const;

/** Settings › Staff Settings › Timecards (legacy shell title "Timecard"). */
export const TIMECARD_TABS = [
  { label: 'Employee Breaks', to: '/l/$slug/settings/staff-settings/timecards/employee-breaks' },
  { label: 'Overtime Settings', to: '/l/$slug/settings/staff-settings/timecards/overtime' },
  { label: 'Report Settings', to: '/l/$slug/settings/staff-settings/timecards/report-settings' },
] as const;

/**
 * POS Functions' tabs, in legacy's order (PosFunctionsIndex.vue). Pacing shows without legacy's Split flag
 * betaOrderPacing: the setting alone switches order pacing (L256). Legacy's last tab, Messaging, edits only the
 * customer display's marketing SMS signup (`marketing_messages_sms*`), which Radish Next hides until a consent-compliant
 * capture exists (L43, G-R7; Q-SMS-1), so it has no tab (lane V's round 14 row, answered 2026-10-13).
 */
export const POS_FUNCTIONS_TABS = [
  { label: 'Register Settings', to: '/l/$slug/settings/order-checkout/pos-functions/register-settings' },
  { label: 'Dining Options', to: '/l/$slug/settings/order-checkout/pos-functions/dining-options' },
  { label: 'Tender Types', to: '/l/$slug/settings/order-checkout/pos-functions/tender-types' },
  { label: 'Pacing', to: '/l/$slug/settings/order-checkout/pos-functions/pacing' },
  { label: 'Drawers', to: '/l/$slug/settings/order-checkout/pos-functions/drawers' },
  { label: 'Voids', to: '/l/$slug/settings/order-checkout/pos-functions/voids' },
  { label: 'ID Verification', to: '/l/$slug/settings/order-checkout/pos-functions/id-verification' },
] as const;

export const LAYOUTS = {
  'order-checkout.tips.customer-tips': {
    id: 'order-checkout.tips.customer-tips',
    screenId: 'backoffice.settings.order-checkout.tips.customer-tips',
    tabs: TIPS_TABS,
    intro: 'Set the tipping options displayed to your customers on the customer-facing display.',
    sections: [
      {
        heading: 'Tip Amounts',
        fields: [
          { key: 'payments.tips.use_default_presets', flavour: 'radio', options: { true: 'Default percentage amounts (15%, 20%, 25%)', false: 'Set fixed percentage amounts' } },
        ],
      },
      {
        // Legacy TipsCustomAmounts.vue, v-shown with its own rule while fixed amounts are chosen: a group of its own with a
        // bold sub-heading, the description, and the three MoneyInputs stacked (B1505).
        heading: 'Percentage Amounts',
        visibleWhen: (v) => v['payments.tips.use_default_presets'] === false,
        fields: [{
          key: 'payments.tips.custom_presets', label: 'Set fixed percentage amounts',
          description: 'Enter the fixed percentage values you want displayed to your customers. Preset amounts set to 0% will be hidden.',
        }],
      },
      {
        heading: 'No Tip Option',
        description: 'Choose the label that want displayed on the “No Tip” Option',
        fields: [{ key: 'payments.tips.no_tip_label', flavour: 'radio', options: { next_time: '“Next Time” (more friendly)', no_tip: '“No Tip” (more direct)' } }],
      },
      {
        heading: 'Tip Reactions',
        fields: [{
          key: 'payments.tips.reactions_enabled',
          label: 'Enable tip reactions (gratitude emojis)',
          description: 'Shows different smiley faces next to each tip option, with happier expressions for higher tip amounts.',
        }],
      },
    ],
  },
  'order-checkout.tips.staff-tips': {
    id: 'order-checkout.tips.staff-tips',
    screenId: 'backoffice.settings.order-checkout.tips.staff-tips',
    tabs: TIPS_TABS,
    intro: 'Control staff-level tipping features and functions',
    sections: [
      {
        heading: 'Payroll Tips',
        fields: [{
          // Legacy StaffTipsSettings.vue: a labelled BaseCheckbox (medium weight) with its description indented under it (B786).
          key: 'labor.tips.payroll_from_tenders', label: 'Show collected card tips on the Payroll report', labelledCheckbox: true,
          description: "Payroll tips are calculated from card tips on each employee's completed orders instead of recorded tip payouts. Tip Payout is hidden from the Register's Cash Management while this is enabled.",
        }],
      },
      {
        heading: 'Tip Tracking',
        // Legacy v-shows the whole wrapper and its q-separator while Payroll Tips is ticked, not just the checkbox (B843).
        visibleWhen: (v) => v['labor.tips.payroll_from_tenders'] !== true,
        fields: [{
          key: 'labor.tips.pooling_enabled', label: 'Enable tracking for employee tip payouts', labelledCheckbox: true,
          description: 'Allows managers to track and distribute cash tip payouts to individual employees.',
        }],
      },
      {
        heading: 'Table Service Tips',
        fields: [{
          key: 'payments.receipts.carry_to_table_prompt', label: 'Enable "Carry to Table" button',
          description: "The 'Carry to Table' button lets staff skip the tip screen when processing payments and delivering customer checks. This allows customers to add their tip directly on the printed receipt.",
        }],
      },
      {
        heading: 'Tip Paid Ins',
        fields: [{
          key: 'labor.tips.paid_in_enabled', label: 'Enable Tip Paid Ins in Manager Activities (reporting only)',
          description: 'Allows managers to report tips from a third-party source (e.g., online ordering).',
        }],
      },
    ],
  },
  // Legacy RegisterSettings.vue and AutoGratuitySettings.vue (pages/settings-order-staff-timecards.md §1.1; lane V r5):
  // legacy's groups, order and copy verbatim. Not on legacy's page: Server Cash Banks (kept last). Bar Tab Attached Cards
  // shows for every gateway here (legacy: Datacap and Sandbox only).
  'order-checkout.register-settings': {
    id: 'order-checkout.register-settings',
    screenId: 'backoffice.settings.order-checkout.register-settings',
    tabs: POS_FUNCTIONS_TABS,
    tabsLabel: 'POS Functions',
    // Legacy RegisterSettings.vue stacks each group's settings with no rule between them (rules only between groups), except
    // Tables, Tax Settings and Kitchen, which put a q-separator between their two (B1499).
    fieldRules: false,
    intro: 'Configure front-of-house register functionality, including payment options, item/modifier display settings, table management, and other functions.',
    sections: [
      {
        heading: 'Payment Settings',
        fields: [
          {
            key: 'orders.split_check.enabled', label: 'Enable split checks',
            description: 'Allow staff to split the bill into multiple checks. This feature will add the Split Check Editor to the Register, allowing users to separate items onto different checks.',
          },
          {
            key: 'orders.split_check.split_quantity', label: 'Split item quantities across checks',
            description: 'When moving a multi-quantity item to another check, prompt for how many units to move instead of moving the entire line. Lets staff split part of a quantity onto a separate check.',
            visibleWhen: (v) => v['orders.split_check.enabled'] === true,
          },
          { key: 'payments.tips.signature_on_customer_display', label: 'Require signature capture', description: 'Display the signature capture screen on the customer-facing display' },
        ],
      },
      {
        heading: 'Customer Facing Display',
        fields: [{
          key: 'register.customer_display.mode_switching', label: 'Allow CFD mode switching',
          description: "Let staff switch a paired customer-facing display between Customer Display and POS mode from the Register's admin menu. Adds Connect Display / Disconnect Display actions to that menu.",
        }],
      },
      {
        heading: 'Bar Tab Attached Cards',
        fields: [{
          key: 'payments.bar_tabs.attach_card', label: 'Bar Tab Attached Cards',
          description: "Attach a guest's card to an open tab with a pre-auth, then automatically extend the hold as items are added. Adds a card-attach icon button to the cart header.",
        }],
      },
      {
        heading: 'Void Reason',
        fields: [{ key: 'orders.void.require_reason_for_sent_items', label: 'Void Reason on Item Removal', description: 'Prompt for a void reason when removing an item that has already been sent to the kitchen.' }],
      },
      {
        heading: 'Cash Management',
        fields: [{
          key: 'cash.management.note_required', label: 'Require Cash Management description',
          description: 'Require staff to enter a description when performing a Paid In or Paid Out on the Register. The transaction cannot be completed until a description is provided.',
        }],
      },
      {
        heading: 'Included Modifiers',
        fields: [
          {
            key: 'catalog.modifiers.use_included', label: 'Enable included modifiers',
            description: 'Allow administrators to set default modifiers that come automatically included with menu items. Pre-selected modifiers appear tagged as "included" in the POS, with options to include or omit them on individual menu items.',
          },
          { key: 'catalog.modifiers.included_on_top', label: 'Re-Order included modifiers to the Top', titleMargin: true, visibleWhen: (v) => v['catalog.modifiers.use_included'] === true },
        ],
      },
      {
        heading: 'Tables',
        fieldRules: true,
        fields: [
          {
            key: 'floor.table_mapping', label: 'Enable Table Mapping',
            description: 'Create an interactive floor plan to visually assign orders to specific tables. Staff can track table status, monitor dining progress, and manage seating more efficiently.',
          },
          {
            // Legacy copy "Allows hostess assigning orders to server banks" (Q-SET-11, grammar fixed).
            key: 'floor.hostess_mode', label: 'Enable hostess mode', description: "Lets a hostess seat a party at a table on a server's bank.",
            visibleWhen: (v) => v['floor.table_mapping'] === true,
          },
        ],
      },
      {
        heading: 'Item Management',
        fields: [{
          key: 'orders.items.availability_mode', label: 'Enable Item Management Mode',
          description: "Allow front office staff to modify inventory status directly from the register interface. When enabled, permitted users can mark items as 86'd (out of stock) or Available.",
          hint: 'Recommended for establishments where front office staff need to quickly mark items out of stock.',
        }],
      },
      {
        heading: 'Tax Settings',
        fieldRules: true,
        fields: [
          { key: 'orders.tax_profile.exclusive_item_override', label: 'Tax-exclusive cart override', description: 'Set whole cart to tax-exclusive when a tax-exclusive item is added' },
          {
            key: 'orders.tax_profile.show_in_product_card', label: 'Display tax profile on individual products',
            description: 'Adds a “Tax Profile” tab to the Product Card, allowing users to change the tax profile on individual items',
          },
        ],
      },
      {
        heading: 'Kitchen',
        fieldRules: true,
        fields: [
          {
            key: 'kitchen.hold_items.enabled', label: 'Kitchen hold & fire',
            description: 'Allow staff to hold individual items in an order so they are not sent to the kitchen until explicitly fired. Useful for multi-course service, bar-first workflows, or any scenario where the kitchen should not prepare certain items immediately.',
          },
          {
            key: 'receipts.kitchen.use_sort_priority', label: 'Use sort priority for kitchen receipt',
            description: 'When enabled, the kitchen receipt items will be sorted by the sort priority of their category. Disable to sort by the order the items were added to the cart.',
          },
          // B1904 (legacy RegisterSettings.vue 512-578, cfd4307b3): the RCP-26 void ticket, and only while it is on, its
          // auto-print window as a setting of its own under it.
          {
            key: 'receipts.kitchen.void_ticket.enabled', label: 'Print a kitchen void ticket',
            description: "When an order is voided, or an item the kitchen already has is removed or reduced, print a VOID ticket to that item's kitchen printers.",
          },
          {
            key: 'receipts.kitchen.void_ticket.auto_print_minutes', label: 'Auto-print within',
            description: 'Minutes after the order was started during which void tickets print automatically. Later voids can still be printed from the void dialog on the Register.',
            visibleWhen: (v) => v['receipts.kitchen.void_ticket.enabled'] === true,
            underCheckbox: {
              fieldLabel: 'Minutes', suffix: 'min',
              // Legacy's kitchenVoidWindowRules, in its order (the API stores what it is sent, so the form is the guard).
              rules: [
                (t) => (t.trim() !== '' && Number.isInteger(Number(t))) || 'Enter a whole number of minutes',
                (t) => Number(t) >= 1 || 'Must be at least 1 minute',
                (t) => Number(t) <= 1440 || 'Cannot exceed 1440 minutes (24 hours)',
              ],
            },
          },
        ],
      },
      {
        heading: 'Gratuity',
        fields: [{
          key: 'orders.gratuity.manual_enabled', label: 'Allow staff to add a gratuity',
          description: 'Shows the “Add Gratuity” action in the register’s cart menu, letting staff put a gratuity on a check by hand. Disable to remove it. “Remove Gratuity” stays available either way, so a gratuity already on a check can still be cleared.',
        }],
      },
      {
        heading: 'Auto Gratuity',
        fields: [
          {
            // Legacy AutoGratuitySettings.vue: a labelled BaseCheckbox (the 20 px box 10 px into a 40 px cell, the label medium
            // weight beside it) with the description 40 px in under it, not a UiCustomSetting (B1500).
            key: 'orders.gratuity.auto_enabled', label: 'Enable auto gratuity', labelledCheckbox: true,
            description: 'Automatically apply a gratuity percentage when the order subtotal meets or exceeds the threshold amount.',
          },
          // Legacy AutoGratuitySettings.vue: "Minimum order subtotal" (MoneyInput) before the percentage.
          // B1948: the three share legacy's `q-pl-sm row q-col-gutter-md`, col-sm-4 / col-sm-4 / col-sm-8.
          { key: 'orders.gratuity.auto_threshold', label: 'Minimum order subtotal', cols: 4, visibleWhen: (v) => v['orders.gratuity.auto_enabled'] === true },
          { key: 'orders.gratuity.auto_rate', label: 'Gratuity percentage', cols: 4, wholePercent: { placeholder: '18' }, visibleWhen: (v) => v['orders.gratuity.auto_enabled'] === true },
          // Legacy's q-select multiple (use-chips) over the dining options, empty for every one (SETG-019).
          {
            key: 'orders.gratuity.auto_dining_option_ids', label: 'Apply to dining options', choicesFrom: 'dining-options', cols: 8,
            fieldHint: 'Leave empty to apply to all dining options', visibleWhen: (v) => v['orders.gratuity.auto_enabled'] === true,
          },
        ],
      },
    ],
  },
  // Legacy DrawersIndex.vue and ServerBanks.vue (SCR-147): the Server Cash Banks group, each row under the switch while it
  // is on, the auto-logout countdown under its own switch. Legacy's "Return to logged in drawer" is left out: no register
  // reads it (dead, B226 / L366).
  'order-checkout.drawers': {
    id: 'order-checkout.drawers',
    screenId: 'backoffice.settings.order-checkout.drawers',
    tabs: POS_FUNCTIONS_TABS,
    tabsLabel: 'POS Functions',
    intro: 'Configure drawer settings, cash handling procedures, and accountability options for registers and staff members.',
    sections: [{
      heading: 'Server Cash Banks',
      fields: [
        {
          key: 'cash.server_banks.enabled', label: 'Enable Server Cash Banks (closeout reconciliation)',
          description: 'Allow staff to track cash handling without an opening balance for efficient cash reconciliation (without a drawer) tip payouts. When disabled, only standard register drawers can be used.',
        },
        {
          key: 'cash.server_banks.disable_cash', label: 'Disable cash payments on server banks',
          description: "When on, servers using a server bank can't take cash on the Pay screen — the cash options are hidden and staff are prompted to have a manager process the payment.",
          visibleWhen: (v) => v['cash.server_banks.enabled'] === true,
        },
        {
          key: 'cash.server_banks.login_on_register', label: 'Enable server bank login screen on Registers',
          description: 'Enables the server login user interface, allowing servers to log into any cash-drawer-based device to process orders and take payments, which report to their individual cash bank (not the open cash drawer on the device).',
          visibleWhen: (v) => v['cash.server_banks.enabled'] === true,
        },
        {
          key: 'cash.server_banks.closeout_without_tip_payout', label: 'Include an option to reconcile cash (collect/pay) without tip payout',
          description: 'When enabled, the shift closeout shows separate buttons for cash reconciliation (without tips) and with tip payouts. Both options automatically calculate whether the server owes the house or the house owes the server, but allow you to process the collection/payout of cash from orders and tip payouts independently.',
          visibleWhen: (v) => v['cash.server_banks.enabled'] === true,
        },
        {
          key: 'cash.server_banks.auto_sign_out', label: 'Auto-Logout for Server Cash Banks',
          description: 'Enable auto-prompt logout countdown after each transaction for server cash banks. Helps maintain security and prevents unauthorized access.',
          visibleWhen: (v) => v['cash.server_banks.enabled'] === true && v['cash.server_banks.login_on_register'] === true,
        },
        {
          key: 'cash.server_banks.auto_sign_out_seconds', label: 'Countdown timer', options: { 10: '10 seconds', 20: '20 seconds', 30: '30 seconds' },
          visibleWhen: (v) => v['cash.server_banks.enabled'] === true && v['cash.server_banks.login_on_register'] === true && v['cash.server_banks.auto_sign_out'] === true,
        },
      ],
    }],
  },
  // Legacy PacingSettings.vue (pages/settings-order-staff-timecards.md §1.4; field note
  // backoffice.settings.order-checkout.pacing, walked 2026-10-12): Meal Courses (the switch; PacingPage puts Add Course
  // and the courses under it while it is ticked), then the Course Prompt, shown whether courses are on or off as legacy
  // shows it. PacingPage adds the Print Options row.
  'order-checkout.pacing': {
    id: 'order-checkout.pacing',
    screenId: 'backoffice.settings.order-checkout.pacing',
    tabs: POS_FUNCTIONS_TABS,
    tabsLabel: 'POS Functions',
    intro: 'Control how items are sent to the kitchen with meal pacing options.',
    sections: [
      {
        heading: 'Meal Courses',
        fields: [{
          // Legacy PacingSettings.vue: a dense BaseCheckbox in UiCustomSetting, its title with q-mb-sm (B785).
          key: 'kitchen.courses.enabled', label: 'Enable Courses', denseCheckbox: 'title-gap',
          description: 'Organize menu items into custom courses (appetizers, mains, desserts, etc.) for streamlined order management. When the order is sent, the system displays a prompt requiring that the user select which courses–and the items assigned to them–are fired to the kitchen.',
        }],
      },
      {
        heading: 'Course Prompt',
        description: 'Prompt users to assign courses to all items before sending order to the kitchen.',
        // Legacy: three plain 40 px q-radios 10 px under the description, in UiCustomSetting's column (B785).
        tight: true,
        fields: [{ key: 'kitchen.courses.prompt_mode', label: 'Course Prompt', flavour: 'radio', plainLabels: true, options: { off: 'Off', optional: 'Optional', required: 'Required' } }],
      },
    ],
  },
  // Legacy IdVerifications.vue (§1.7; SCR-149): one row, the switch; IdVerificationPage puts Add Verification List,
  // Preview and the lists under it while it is ticked. Wording from legacy's `en` strings (en-US lacks them: Q-SET-04).
  'order-checkout.id-verification': {
    id: 'order-checkout.id-verification',
    screenId: 'backoffice.settings.order-checkout.id-verification',
    tabs: POS_FUNCTIONS_TABS,
    tabsLabel: 'POS Functions',
    sections: [{
      heading: 'ID Verification',
      fields: [{
        // Legacy IdVerifications.vue: a dense BaseCheckbox in UiCustomSetting, its title with q-mb-sm (B784).
        key: 'orders.id_verification.enabled', label: 'Enable ID Verification', denseCheckbox: 'title-gap',
        description: 'Automatically prompt staff to check customer IDs for age-restricted products. Bulk assign items in the Back Office, and the register will display a verification modal before completing sales—ensuring compliance for alcohol, tobacco, and other restricted items.',
      }],
    }],
  },
  // Legacy PrintOptionsSettings.vue (§4): Receipts (`print_new_extra_line`, `display_item_notes_on_customer_receipts`), then
  // Coursing/Pacing while courses are on. Legacy's "Never print" option leaves its parenthesis open; it is closed here (B158).
  'order-checkout.print-options': {
    id: 'order-checkout.print-options',
    screenId: 'backoffice.settings.order-checkout.print-options',
    intro: 'Configure printing settings for customer and order ticket receipts.',
    reads: ['kitchen.courses.enabled'],
    sections: [{
      heading: 'Receipts',
      fields: [
        {
          key: 'receipts.extra_line_spacing', label: 'Increase vertical spacing',
          description: 'Enabling this adds an extra space between items for clear separation on order, check, and ticket receipts.',
        },
        {
          key: 'receipts.customer.show_item_notes', label: 'Display item notes',
          description: 'Show special instructions and item notes on customer receipts. When enabled, any custom notes added to menu items during order entry will be printed on receipts for customer reference.',
        },
      ],
    }, {
      heading: 'Coursing/Pacing',
      // Legacy shows it with order pacing (Split betaOrderPacing); here order pacing is Enable Courses (L256).
      visibleWhen: (v) => v['kitchen.courses.enabled'] === true,
      fields: [
        {
          key: 'receipts.kitchen.course_print_mode', label: 'Choose when to print the entire order receipt along with course fires.',
          options: {
            first_fire: 'Print with first course only',
            every_fire: 'Print (reprint) with every course fire',
            never: 'Never print (only prints individual course receipts without the entire order receipt)',
          },
        },
        { key: 'receipts.kitchen.separate_course_tickets', label: 'Print separate receipts for each course' },
      ],
    }],
  },
  'staff-settings.timecards.report-settings': {
    id: 'staff-settings.timecards.report-settings',
    screenId: 'backoffice.settings.staff-settings.timecards.report-settings',
    tabs: TIMECARD_TABS,
    tabsLabel: 'Timecards',
    intro: 'Control what appears on employee-related reports for this location.',
    // Legacy ReportSettings.vue is a one-column stack (measured beside legacy on the DDEV, lane D4 2026-10-15), and its
    // description keeps the 7shifts sentence (en-US `externalEmployeeIdDescription`, verbatim).
    stacked: true,
    sections: [{
      heading: 'External Employee ID',
      description: 'Show an External ID field on employees and include it as a column on the payroll report, for mapping your staff to an outside payroll service. The identifier is stored exactly as you enter it and is never validated against the other service. Where the 7shifts integration is connected it maintains this value, so the field is read-only on employees at those locations.',
      fields: [{ key: 'labor.reports.external_employee_id_enabled', label: 'Enable External Employee ID at this location' }],
    }],
  },
  // Settings › Staff Settings › Scheduling is its own page (SchedulingSettingsPage.tsx): legacy's one-column layout.
  // Settings › Staff Settings › Timecards › Overtime Settings is its own page (OvertimeSettingsPage.tsx): legacy's three
  // inline sentences and its Seventh-day Rule block (B781).
  'location.business-day': {
    id: 'location.business-day',
    screenId: 'backoffice.settings.business-day',
    intro: 'When your business day starts. Sales, timecards and reports count toward the business day they fall in.',
    sections: [{ heading: 'Business Day', fields: [{ key: 'business_day.start_time', label: 'Business day starts at' }] }],
  },
} as const satisfies Readonly<Record<string, SettingsLayout>>;

export type LayoutId = keyof typeof LAYOUTS;
